import React, { useEffect, useState } from "react";
import { Alert, Box, Button, CircularProgress, Dialog, DialogActions, DialogContent, DialogTitle, Stack, Table, TableBody, TableCell, TableContainer, TableHead, TablePagination, TableRow, TextField, Typography } from "@mui/material";
import { api, errorMessage } from "./api";
import { QueryResult, UserHistoryEvent } from "./types";
import { formatActivationDate, lastActiveHelp } from "./activationDates";

export default function UserHistory({ userId, csrf, refreshVersion }: { userId: string; csrf: string; refreshVersion: number }) {
  const [page, setPage] = useState(0);
  const [result, setResult] = useState<QueryResult<UserHistoryEvent> | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState("");
  const [retry, setRetry] = useState(0);
  const [activation, setActivation] = useState<UserHistoryEvent | null>(null);
  useEffect(() => {
    let active = true;
    setLoading(true);
    setError("");
    setActivation(null);
    api.userHistory(userId, page + 1, csrf).then(data => {
      if (active) setResult(data);
    }).catch(requestError => {
      if (active) { setResult(null); setError(errorMessage(requestError)); }
    }).finally(() => { if (active) setLoading(false); });
    return () => { active = false; };
  }, [userId, csrf, page, refreshVersion, retry]);
  return <Box sx={{ mt: 3 }}>
    <Stack direction="row" justifyContent="space-between" alignItems="center">
      <Typography component="h4" variant="h6">User history</Typography>
      <Button disabled={loading} onClick={() => setRetry(value => value + 1)}>Refresh history</Button>
    </Stack>
    <Typography variant="body2" color="text.secondary" sx={{ mt: 1, mb: 2 }}>
      Recorded account, checkout, license, activation, support, and administrator activity. Checkout status is current; license dates show creation, not claim dates. Login history is not recorded.
    </Typography>
    {error && <Alert severity="error">{error}</Alert>}
    {loading ? <CircularProgress size={24} aria-label="Loading user history" /> : result && <>
      {result.items.length === 0 ? <Typography>No recorded history found.</Typography> : <TableContainer>
        <Table size="small" aria-label="User history"><TableHead><TableRow><TableCell>Date</TableCell><TableCell>Activity</TableCell><TableCell>Administrator</TableCell></TableRow></TableHead>
          <TableBody>{result.items.map(event => <TableRow key={event.id}>
            <TableCell sx={{ whiteSpace: "nowrap" }}>{new Date(event.occurred_at.endsWith("Z") ? event.occurred_at : `${event.occurred_at}Z`).toLocaleString()}</TableCell>
            <TableCell>{event.category === "activation" ? <>
              <Button size="small" sx={{ p: 0, textAlign: "left" }} onClick={() => setActivation(event)}>{event.description}</Button>
              <Typography variant="caption" color="text.secondary" sx={{ display: "block" }} title={lastActiveHelp}>
                Last active: {formatActivationDate(event.last_active_at)}
              </Typography>
            </> : event.description}</TableCell><TableCell>{event.actor || "—"}</TableCell>
          </TableRow>)}</TableBody>
        </Table>
      </TableContainer>}
      <TablePagination component="div" count={result.total} page={page} rowsPerPage={25} rowsPerPageOptions={[25]} onPageChange={(_event, next) => setPage(next)} />
    </>}
    <Dialog open={Boolean(activation)} onClose={() => setActivation(null)} aria-labelledby="activation-details-title" fullWidth maxWidth="sm">
      <DialogTitle id="activation-details-title">Activation details</DialogTitle>
      <DialogContent>
        <Stack spacing={2} sx={{ pt: 1 }}>
          <Typography>{activation?.description}</Typography>
          <TextField label="PC name" value={activation?.machine_name || "Not recorded"} InputProps={{ readOnly: true }} fullWidth />
          <TextField label="PC ID" value={activation?.machine_id || "Not recorded"} InputProps={{ readOnly: true }} fullWidth multiline />
          <TextField label="Operating system" value={activation?.machine_os || "Not recorded"} InputProps={{ readOnly: true }} fullWidth />
          <TextField label="Last active" value={formatActivationDate(activation?.last_active_at)} helperText={lastActiveHelp} InputProps={{ readOnly: true }} fullWidth />
        </Stack>
      </DialogContent>
      <DialogActions><Button onClick={() => setActivation(null)}>Close</Button></DialogActions>
    </Dialog>
  </Box>;
}
